<section class="page">
  <header class="page-header">
    <div>
      <h1>Configuration</h1>
      <p>Points rates, rent due reminders, subscription payment provider, and Kobble merchant wallet</p>
    </div>
    <button class="btn btn-secondary" type="button" (click)="reload()" [disabled]="loading()">
      Refresh
    </button>
  </header>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }
  @if (success()) {
    <div class="alert alert-success">{{ success() }}</div>
  }

  <div class="grid">
    <div class="panel">
      <h2>Points &amp; merchant wallet</h2>
      @if (loading() && !config()) {
        <p class="muted">Loading…</p>
      } @else {
        <form [formGroup]="pointsForm" (ngSubmit)="savePoints()" class="config-form">
          <label>
            <span>Earn points per AUD (card spend)</span>
            <input type="number" formControlName="points_earn_per_aud" min="1" step="1" />
          </label>
          <label>
            <span>Points per AUD redeem (e.g. 100 = $1)</span>
            <input type="number" formControlName="points_per_aud" min="1" step="1" />
          </label>
          <p class="hint full">
            Redeem rate is global: set <code>100</code> for 100 points = $1 towards rent,
            or <code>200</code> for 200 points = $1. Partner earn rates (pts per $1 spent) are
            configured on each Shopping Partner.
          </p>
          <label class="full">
            <span>Merchant Kobble wallet ID</span>
            <input
              type="text"
              formControlName="merchant_kobble_wallet_id"
              placeholder="UUID — set manually if Kobble client_wallet_id is null"
              autocomplete="off"
            />
          </label>
          <label class="full">
            <span>Subscription payment provider</span>
            <select formControlName="subscription_payment_provider">
              <option value="stripe">Stripe (card checkout)</option>
              <option value="rentwize">Rentwize wallet (Kobble INTER_WALLET)</option>
            </select>
          </label>
          <p class="hint">
            Applies to subscription billing only. Rent and transfers always use the Rentwize wallet.
            Rentwize mode requires a merchant wallet ID above.
          </p>
          <button class="btn btn-primary" type="submit" [disabled]="savingPoints()">
            {{ savingPoints() ? 'Saving…' : 'Save configuration' }}
          </button>
        </form>

        @if (config(); as c) {
          <dl class="meta stored">
            <div>
              <dt>Stored client</dt>
              <dd>{{ c.merchant_kobble_client_name || '—' }}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{{ c.merchant_kobble_client_email || '—' }}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{{ c.merchant_kobble_client_status || '—' }}</dd>
            </div>
            <div>
              <dt>Client ID</dt>
              <dd class="mono">{{ c.merchant_kobble_client_id || '—' }}</dd>
            </div>
            <div>
              <dt>External ID</dt>
              <dd class="mono">{{ c.merchant_kobble_client_external_id || '—' }}</dd>
            </div>
            <div>
              <dt>Wallet ready</dt>
              <dd>
                <span class="badge" [attr.data-status]="c.merchant_configured ? 'approved' : 'rejected'">
                  {{ c.merchant_configured ? 'yes' : 'no' }}
                </span>
              </dd>
            </div>
          </dl>
        }
      }
    </div>

    <div class="panel">
      <h2>Rent due reminders</h2>
      <p class="hint">
        Sends an in-app notification and FCM push before each user's next rent due date.
        Lead time is stored as hours (e.g. 24 = 1 day before).
      </p>
      <form [formGroup]="reminderForm" (ngSubmit)="saveReminders()" class="config-form">
        <label class="full checkbox-row">
          <input type="checkbox" formControlName="enabled" />
          <span>Enable rent due reminders</span>
        </label>
        <label>
          <span>Lead time</span>
          <input type="number" formControlName="lead_value" min="1" step="1" />
        </label>
        <label>
          <span>Unit</span>
          <select formControlName="lead_unit">
            <option value="hours">Hours</option>
            <option value="days">Days</option>
          </select>
        </label>
        <p class="hint full">
          Stored as
          <strong>{{ reminderHoursPreview() }}</strong>
          hour(s) (max 720 hours / 30 days).
        </p>
        <button class="btn btn-primary" type="submit" [disabled]="savingReminders()">
          {{ savingReminders() ? 'Saving…' : 'Save reminder settings' }}
        </button>
      </form>
    </div>

    <div class="panel">
      <h2>Kobble clients</h2>
      <p class="hint">
        From <code>GET /customers/v1/clients</code>. Sync stores the selected client into
        <code>platform_settings</code>. Field name on Kobble is <code>client_wallet_id</code>.
      </p>

      @if (!clients().length && !loading()) {
        <p class="muted">No clients returned from Kobble</p>
      } @else {
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Client wallet</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (c of clients(); track c.id) {
                <tr [class.selected]="selectedClientId() === c.id">
                  <td>
                    <input
                      type="radio"
                      name="client"
                      [checked]="selectedClientId() === c.id"
                      (change)="selectClient(c.id)"
                    />
                  </td>
                  <td>{{ c.name || '—' }}</td>
                  <td>{{ c.email || '—' }}</td>
                  <td>{{ c.status || '—' }}</td>
                  <td class="mono">{{ c.client_wallet_id || c.wallet_id || 'null' }}</td>
                  <td>
                    <button
                      class="btn btn-secondary"
                      type="button"
                      (click)="syncClient(c.id)"
                      [disabled]="syncing()"
                    >
                      Sync
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <button
          class="btn btn-primary sync-selected"
          type="button"
          (click)="syncClient()"
          [disabled]="syncing() || !selectedClientId()"
        >
          {{ syncing() ? 'Syncing…' : 'Sync selected client' }}
        </button>
      }
    </div>
  </div>
</section>
